import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DELETE_REASONS,
  REASON_WITH_TEXT,
  REASON_TEXT_MAX_LENGTH,
} from '../../util/listingDeletion';

import { Button, FieldRadioButton, FieldTextInput, Form, Modal } from '../../components';

import css from './DeleteListingModal.module.css';

/**
 * "Slett annonse": the seller picks a reason and confirms. "Annet" opens a
 * short, optional text field. The deletion runs on the server, which refuses
 * while an order on the listing is unfinished; the modal stays open and says
 * why. On phones it is a compact card in the middle of the screen, not a
 * full-screen page (Guro, 26 October).
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The id of the modal
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} [props.focusElementId] - Element to focus when the modal closes
 * @param {function} props.onCloseModal - Closes the modal
 * @param {function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {function} props.onDeleteListing - Called with the reason and the optional text; returns a promise
 * @returns {JSX.Element} Delete listing modal component
 */
const DeleteListingModal = props => {
  const intl = useIntl();
  const [inProgress, setInProgress] = useState(false);
  const [errorId, setErrorId] = useState(null);
  const {
    className,
    rootClassName,
    id,
    isOpen,
    focusElementId,
    onCloseModal,
    onManageDisableScrolling,
    onDeleteListing,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const handleClose = () => {
    setErrorId(null);
    onCloseModal();
  };

  const handleSubmit = ({ reason, reasonText }) => {
    setInProgress(true);
    setErrorId(null);
    const text = reason === REASON_WITH_TEXT ? (reasonText || '').trim() : '';
    return onDeleteListing(reason, text || undefined)
      .then(() => {
        setInProgress(false);
      })
      .catch(e => {
        setInProgress(false);
        const isBlocked = e?.error === 'active-order';
        setErrorId(isBlocked ? 'DeleteListingModal.activeOrder' : 'DeleteListingModal.failed');
      });
  };

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      scrollLayerClassName={css.scrollLayer}
      closeNavClassName={css.closeNav}
      closeButtonRootClassName={css.close}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      focusElementId={focusElementId}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'DeleteListingModal.close' })}
    >
      <FinalForm
        onSubmit={handleSubmit}
        render={({ handleSubmit: onSubmit, values }) => (
          <Form className={css.form} onSubmit={onSubmit}>
            {/* Everything but the button scrolls on a short screen. */}
            <div className={css.body}>
              <p className={css.modalTitle}>
                <FormattedMessage id="DeleteListingModal.title" />
              </p>
              <p className={css.modalMessage}>
                <FormattedMessage id="DeleteListingModal.message" />
              </p>
              <fieldset className={css.reasons}>
                <legend className={css.reasonsLegend}>
                  <FormattedMessage id="DeleteListingModal.reasonLabel" />
                </legend>
                {DELETE_REASONS.map(reason => (
                  <FieldRadioButton
                    key={reason}
                    className={css.reason}
                    id={`${id}.reason.${reason}`}
                    name="reason"
                    value={reason}
                    label={intl.formatMessage({ id: `DeleteListingModal.reason.${reason}` })}
                  />
                ))}
                {values.reason === REASON_WITH_TEXT ? (
                  <FieldTextInput
                    className={css.reasonText}
                    id={`${id}.reasonText`}
                    name="reasonText"
                    type="textarea"
                    maxLength={REASON_TEXT_MAX_LENGTH}
                    aria-label={intl.formatMessage({
                      id: `DeleteListingModal.reason.${REASON_WITH_TEXT}`,
                    })}
                  />
                ) : null}
              </fieldset>
              {errorId ? (
                <p className={css.error} role="alert">
                  <FormattedMessage id={errorId} />
                </p>
              ) : null}
            </div>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={inProgress}
              disabled={!values.reason || inProgress}
            >
              <FormattedMessage id="DeleteListingModal.submit" />
            </Button>
          </Form>
        )}
      />
    </Modal>
  );
};

export default DeleteListingModal;
